import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { TaskType } from '@pms/shared';
import { tasksService } from '@/services/tasks.service';
import { getErrorMessage } from '@/lib/errors';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@pms/shared';
import { Switch } from '@/components/ui/switch';
import { TYPE_META } from '@/components/tasks/taskConstants';

const TYPE_DESCRIPTION: Record<TaskType, string> = {
  daily: 'Tasks that reset every day at midnight',
  weekly: 'Tasks that reset on a specific day each week',
  monthly: 'Tasks that reset on a specific date each month',
  yearly: 'Tasks that reset on a specific date each year',
  periodic: 'Tasks with a fixed start and end date, no reset. Marked Overdue if missed.',
};

export function TaskTypesTab() {
  const qc = useQueryClient();
  const { has } = usePermission();
  const canEdit = has(PERMISSIONS.TASK_CONFIG);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tasks', 'config', 'task-types'],
    queryFn: () => tasksService.getTaskTypeSettings(),
  });

  const toggle = useMutation({
    mutationFn: (v: { type: TaskType; enabled: boolean }) => tasksService.setTaskTypeEnabled(v.type, v.enabled),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['tasks', 'config', 'task-types'] });
      const prev = qc.getQueryData(['tasks', 'config', 'task-types']);
      qc.setQueryData(['tasks', 'config', 'task-types'], (old: typeof data) =>
        old?.map((r) => (r.task_type === v.type ? { ...r, is_enabled: v.enabled } : r)));
      return { prev };
    },
    onError: (e, _v, ctx) => {
      qc.setQueryData(['tasks', 'config', 'task-types'], ctx?.prev);
      toast.error(getErrorMessage(e));
    },
    onSuccess: () => toast.success('Updated'),
    onSettled: () => qc.invalidateQueries({ queryKey: ['tasks', 'config', 'task-types'] }),
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Task Types</h2>
        <p className="text-sm text-muted-foreground">Enable or disable task types. These are predefined and cannot be deleted or renamed.</p>
      </div>

      <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
        <table className="w-full text-sm">
          <thead className="bg-primary text-xs uppercase tracking-wider text-primary-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Type</th>
              <th className="px-4 py-2.5 text-left font-semibold">Description</th>
              <th className="px-4 py-2.5 text-left font-semibold">Status</th>
              <th className="px-4 py-2.5 text-right font-semibold">Enabled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {isLoading && [...Array(6)].map((_, i) => (
              <tr key={i} className="animate-pulse"><td colSpan={4} className="px-4 py-4"><div className="h-4 rounded bg-muted/60" /></td></tr>
            ))}
            {isError && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
                Couldn’t load task types. <button className="text-primary underline" onClick={() => refetch()}>Retry</button>
              </td></tr>
            )}
            {data?.map((row) => (
              <tr key={row.task_type} className="hover:bg-muted/30">
                <td className="px-4 py-3 font-medium">{TYPE_META[row.task_type].label}</td>
                <td className="px-4 py-3 text-muted-foreground">{TYPE_DESCRIPTION[row.task_type]}</td>
                <td className="px-4 py-3">
                  <span className={row.is_enabled
                    ? 'inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400'
                    : 'inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-500/15 dark:text-slate-400'}>
                    {row.is_enabled ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <Switch
                      checked={row.is_enabled}
                      disabled={!canEdit || toggle.isPending}
                      onChange={(v) => toggle.mutate({ type: row.task_type, enabled: v })}
                      aria-label={`Toggle ${row.task_type}`}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
